import React from 'react';
import { Typography, Row, Col, Card, Tag, Grid } from 'antd';
import { Link } from 'react-router-dom';
import {
  AndroidOutlined,
  WifiOutlined,
  FundProjectionScreenOutlined,
  AlertOutlined,
  WarningOutlined,
  IdcardOutlined,
  CompassOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { IoCarSport } from 'react-icons/io5';
import illustration from '../assets/dashboard-illustration.png';

const { useBreakpoint } = Grid;
const { Title, Text } = Typography;

const SERVICE_CARDS = [
  {
    path: '/vehicle-overview',
    title: 'Vehicle Overview',
    category: 'Status',
    icon: <IoCarSport style={{ fontSize: 22 }} />,
    color: '#2563eb',
    bg: '#eff6ff',
    features: ['Lamp Status', 'Door Status', 'Tyre Pressure'],
  },
  {
    path: '/alerts',
    title: 'Alerts',
    category: 'Monitoring',
    icon: <AlertOutlined style={{ fontSize: 22 }} />,
    color: '#dc2626',
    bg: '#fef2f2',
    features: ['Battery Alert', 'Engine Temperature', 'Low Tyre Pressure'],
  },
  {
    path: '/location-based-services',
    title: 'Location Services',
    category: 'Location',
    icon: <CompassOutlined style={{ fontSize: 22 }} />,
    color: '#0891b2',
    bg: '#ecfeff',
    features: ['Trip History', 'Live Tracking', 'Geo-fencing'],
  },
  {
    path: '/live-logs-simulator',
    title: 'Live Logs',
    category: 'Monitoring',
    icon: <FundProjectionScreenOutlined style={{ fontSize: 22 }} />,
    color: '#7c3aed',
    bg: '#f5f3ff',
    features: ['Vehicle Speed Data', 'Tyre Pressure Data', 'Battery Level Data'],
  },
  {
    path: '/remote-functions',
    title: 'Remote Functions',
    category: 'Control',
    icon: <WifiOutlined style={{ fontSize: 22 }} />,
    color: '#059669',
    bg: '#ecfdf5',
    features: ['Door Lock / Unlock', 'Headlamp On / Off', 'Remote Start / Stop'],
  },
  {
    path: '/dtc-signals',
    title: 'DTC Signals',
    category: 'Diagnostics',
    icon: <WarningOutlined style={{ fontSize: 22 }} />,
    color: '#d97706',
    bg: '#fffbeb',
    features: ['Fault Codes', 'Diagnostic Data', 'Signal History'],
  },
  {
    path: '/valet-mode',
    title: 'Valet Mode',
    category: 'Control',
    icon: <IdcardOutlined style={{ fontSize: 22 }} />,
    color: '#0f766e',
    bg: '#f0fdfa',
    features: ['Speed Limiting', 'Trunk Locking', 'Mode Status'],
  },
  {
    path: '/android-test-client',
    title: 'Android Test Client',
    category: 'Tools',
    icon: <AndroidOutlined style={{ fontSize: 22 }} />,
    color: '#64748b',
    bg: '#f8fafc',
    features: ['Door Lock / Unlock', 'Headlamp On / Off', 'Remote Start / Stop'],
  },
];

const CATEGORY_COLORS = {
  Status: 'blue',
  Monitoring: 'purple',
  Location: 'cyan',
  Control: 'green',
  Diagnostics: 'gold',
  Tools: 'default',
};

const ServiceCard = ({ path, title, category, icon, color, bg, features }) => (
  <Link to={path} style={{ display: 'flex', width: '100%' }}>
    <Card
      hoverable
      style={{
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        width: '100%',
        transition: 'box-shadow 0.18s ease, transform 0.18s ease',
      }}
      styles={{
        body: {
          padding: '16px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        },
      }}
    >
      {/* Icon + title row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 10,
            background: bg,
            border: `1px solid ${color}22`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: '#0f172a',
              lineHeight: '20px',
              wordBreak: 'break-word',
              marginBottom: 4,
            }}
          >
            {title}
          </div>
          <Tag
            color={CATEGORY_COLORS[category]}
            style={{ borderRadius: 20, fontSize: 10, padding: '0 7px', lineHeight: '18px' }}
          >
            {category}
          </Tag>
        </div>
      </div>

      {/* Divider */}
      <div style={{ borderTop: '1px solid #f1f5f9' }} />

      {/* Feature list */}
      <ul
        style={{
          margin: 0,
          padding: 0,
          listStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {features.map((f) => (
          <li
            key={f}
            style={{
              fontSize: 12,
              color: '#64748b',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 7,
              lineHeight: '18px',
            }}
          >
            <span
              style={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                background: color,
                display: 'inline-block',
                flexShrink: 0,
                opacity: 0.65,
                marginTop: 7,
              }}
            />
            <span style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{f}</span>
          </li>
        ))}
      </ul>
    </Card>
  </Link>
);

const Dashboard = () => {
  const screens = useBreakpoint();
  const isMobile = screens.md === false;

  return (
    <div>
      {/* Hero banner */}
      <div
        style={{
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 60%, #0891b2 100%)',
          borderRadius: 12,
          padding: isMobile ? '22px 20px' : '28px 32px',
          marginBottom: 24,
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        {/* Text content */}
        <div style={{ position: 'relative', zIndex: 1, flex: '1 1 auto', minWidth: 0 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.1em',
              color: 'rgba(255,255,255,0.6)',
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            Telematics Portal
          </div>
          <Title
            level={isMobile ? 4 : 3}
            style={{ color: '#fff', margin: 0, marginBottom: 8 }}
          >
            Vehicle Cloud Simulator
          </Title>
          <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: isMobile ? 13 : 14 }}>
            Simulate and test connected vehicle services in real time
          </Text>
          <div style={{ marginTop: 14, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['MQTT', 'AWS IoT', 'Geo-fencing', 'OTA-ready'].map((label) => (
              <span
                key={label}
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  background: 'rgba(255,255,255,0.15)',
                  color: 'rgba(255,255,255,0.9)',
                  padding: '3px 10px',
                  borderRadius: 20,
                  border: '1px solid rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(4px)',
                }}
              >
                {label}
              </span>
            ))}
          </div>
        </div>

        {/* Illustration — hidden on mobile */}
        {!isMobile && (
          <div
            style={{
              flex: '0 0 auto',
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <img
              src={illustration}
              alt="dashboard illustration"
              style={{
                height: 160,
                maxWidth: 260,
                objectFit: 'contain',
                filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.25))',
                userSelect: 'none',
              }}
            />
          </div>
        )}

        {/* Decorative circles — desktop only */}
        {!isMobile && (
          <>
            <div
              style={{
                position: 'absolute',
                right: -40,
                top: -40,
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.05)',
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                right: 80,
                bottom: -60,
                width: 140,
                height: 140,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.04)',
                pointerEvents: 'none',
              }}
            />
          </>
        )}
      </div>

      {/* Section heading */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <BarChartOutlined style={{ color: '#2563eb', fontSize: 15 }} />
        <Text
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: '#475569',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          Services
        </Text>
        <Text style={{ fontSize: 12, color: '#94a3b8' }}>— {SERVICE_CARDS.length} available</Text>
      </div>

      {/* Service cards grid */}
      <Row gutter={[16, 16]}>
        {SERVICE_CARDS.map((card) => (
          <Col key={card.path} xs={24} sm={12} lg={8} xl={6} style={{ display: 'flex' }}>
            <ServiceCard {...card} />
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default Dashboard;
